/**
 * Erasing everything people chose about a feature, and the five things somebody has to know first.
 *
 * Built on the same bones as the survey purge dialog, deliberately: same lock, same shape, same
 * button carrying the verb and the number. What differs is what the damage *is*, and that is worth
 * spelling out, because two numbers live on this screen and swapping them makes the dialog lie:
 *
 * - **`everEnabled` ("Já ligaram") is what gets deleted** — a row in `user_opt_ins` exists because
 *   somebody turned the feature on at least once, so that is the count of rows going away, and the
 *   number the button carries;
 * - **`enabledNow` ("Ligados agora") is who loses the feature** — `isEnabled` lives in that row, so
 *   with no row there is no `isEnabled`, and those people fall back to the default behaviour.
 *
 * The order is deliberate too. The consequence comes **first**, above the counts: people silently
 * going back to the old menu is the gravest thing on this screen, and burying it under an inventory
 * of rows would be a way of not quite saying it. It is a warning and not a block — Victor confirmed
 * that effect is wanted.
 *
 * Then the two things nobody guesses:
 *
 * - **survey answers survive**, so this reopens no survey for anyone. That is the assumption people
 *   will make, and the history rule (R3) looks at the response, not at the opt-in;
 * - **`lastEnabledAt` is the anchor a conditioned survey's delay counts from** (R10). Deleting the
 *   rows kills the clock, so the eligible audience of any survey hung off this feature drops to
 *   zero until people opt in again. The survey was never touched; its clock just lived here.
 *
 * That last sentence names no count on purpose. How many surveys hang off this feature is knowable
 * only from the Surveys module, and this module does not depend on it — the dependency runs the
 * other way (a survey can require a feature, never the reverse), and the adoption payload carries
 * no such field. Importing it here to print a number would invert the layering for a detail the
 * sentence does not need.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import { UTModal, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useState } from 'react';
import type { JSX } from 'react';
import { formatCount } from '../../../Utils/adoptionFormat';

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-bottom: var(--space-s);
  font-size: var(--font-size-sm);
  text-wrap: pretty;
`;

const dangerStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-danger);
  border-left: 3px solid var(--color-danger);
  border-radius: var(--radius-card);
  background: var(--color-danger-subtle);
`;

const keepsStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
`;

const headlineStyle = css`
  margin: 0;
  font-weight: var(--font-weight-semibold);
`;

const lineStyle = css`
  margin: 0;
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const labelStyle = css`
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const keyStyle = css`
  font-family: var(--font-mono);
  font-weight: var(--font-weight-semibold);
`;

const hintStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

export interface OptInPurgeDialogProps {
  featureKey: string;
  featureName: string;
  /** "Ligados agora" — who loses the feature. Never the number on the button. */
  enabledNow: number;
  /** "Já ligaram" — the rows being deleted. This is the number on the button. */
  everEnabled: number;
  isWorking: boolean;
  onPurge: (confirmKey: string) => void;
  onClose: () => void;
}

export const OptInPurgeDialog = ({
  featureKey,
  featureName,
  enabledNow,
  everEnabled,
  isWorking,
  onPurge,
  onClose,
}: OptInPurgeDialogProps): JSX.Element => {
  const [typed, setTyped] = useState('');

  /** Exact, deliberately: the BFF compares with `===`, and so does the button. */
  const unlocked = typed === featureKey;

  const rowsLabel = everEnabled === 1 ? '1 pessoa' : `${formatCount(everEnabled)} pessoas`;

  /*
    Nothing to erase is its own state, not a confirmation for zero rows. Offering a key field to
    guard a no-op is ceremony, and ceremony is what stops being read.
  */
  if (everEnabled === 0) {
    return (
      <UTModal
        open
        position='center'
        maxWidth='480px'
        hasButtonClose
        modalContentId='opt-in-purge-dialog'
        closeButtonId='opt-in-purge-dialog-close'
        title='Apagar dados de uso'
        content={
          <div className={contentStyle}>
            <div className={keepsStyle}>
              <p className={headlineStyle}>Não há o que apagar</p>
              <p className={lineStyle}>
                Ninguém ligou esta funcionalidade ainda, então não há dado de uso guardado.
              </p>
            </div>
          </div>
        }
        customButtons={[{ text: 'Fechar', variant: 'secondary', onClick: onClose }]}
        onClose={onClose}
      />
    );
  }

  return (
    <UTModal
      open
      position='center'
      maxWidth='580px'
      hasButtonClose
      modalContentId='opt-in-purge-dialog'
      closeButtonId='opt-in-purge-dialog-close'
      title='Apagar dados de uso'
      content={
        <div className={contentStyle}>
          <div className={dangerStyle} role='alert'>
            <p className={headlineStyle}>
              {enabledNow === 1
                ? `A 1 pessoa que está com ${featureName} ligada volta ao comportamento antigo, sem aviso.`
                : `As ${formatCount(enabledNow)} pessoas que estão com ${featureName} ligada voltam ao comportamento antigo, sem aviso.`}
            </p>
            <p className={lineStyle}>
              Somem os dados de <strong>{rowsLabel}</strong> — todo mundo que já ligou alguma vez —
              e o histórico junto: primeira ativação, quantas vezes ligou e desligou, e os motivos
              de saída.
            </p>
            <p className={lineStyle}>Não há como desfazer.</p>
          </div>

          <div className={keepsStyle}>
            <p className={headlineStyle}>O que não é apagado</p>
            <p className={lineStyle}>
              A funcionalidade, as traduções e os casos de uso continuam como estão.
            </p>
            <p className={lineStyle}>
              <strong>As respostas de pesquisa também ficam</strong> — e por isso zerar o opt-in{' '}
              <strong>não reabre pesquisa para ninguém</strong>. Quem já respondeu continua
              bloqueado pela regra de histórico, que olha a resposta, não o opt-in.
            </p>
          </div>

          <div className={keepsStyle}>
            <p className={headlineStyle}>O efeito que ninguém adivinha</p>
            <p className={lineStyle}>
              A última ativação de cada pessoa é a <strong>âncora</strong> de onde o atraso de uma
              pesquisa condicionada conta. Apagando as linhas, a âncora morre: toda pesquisa
              condicionada a esta funcionalidade fica com <strong>público elegível zero</strong> até
              as pessoas optarem de novo — e aí o relógio recomeça do começo.
            </p>
            <p className={hintStyle}>
              A pesquisa não foi tocada. O relógio dela é que morava aqui.
            </p>
          </div>

          <div className={fieldStyle}>
            <span className={labelStyle}>
              Para liberar, digite <span className={keyStyle}>{featureKey}</span>
            </span>
            <UTTextField
              id='opt-in-purge-key'
              placeholder={featureKey}
              autoComplete='off'
              value={typed}
              onChange={event => {
                setTyped(event.target.value);
              }}
            />
            <p className={hintStyle}>
              Exato, sem espaço sobrando e respeitando maiúsculas e minúsculas — é o mesmo texto que
              o BFF confere antes de apagar qualquer coisa.
            </p>
          </div>
        </div>
      }
      customButtons={[
        { text: 'Cancelar', variant: 'secondary', disabled: isWorking, onClick: onClose },
        {
          text: `Apagar dados de ${rowsLabel}`,
          state: 'danger',
          loading: isWorking,
          disabled: !unlocked || isWorking,
          onClick: () => {
            onPurge(typed);
          },
        },
      ]}
      onClose={onClose}
    />
  );
};
